/* eslint-disable no-console -- we want to log errors */

import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import type { CancelTokenSource } from '@ori/axios';
import { axios } from '@ori/axios';
import type { Navigation } from '@ori/graphql-schema';
import type {
  NavigationType,
  StaticNavigationNavigationItemDto,
} from '@ori/static-api-client/esm/models';
import type { FC } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useAppConfigurationContext } from './AppConfigurationContext';
import { getCustomAxios } from '~/helpers';
import { useFetchNavigationItems } from '~/hooks/useFetchNavigationItems';
import { useMobileWidth } from '~/hooks/useMobileWidth';
import type { NavigationContextValues, SuggestionsAndResults } from '~/models';
import { BurgerMenuTabNames, JoinLinks, NavigationTypes } from '~/models';

const ABOUT_TILE = 'AboutTiles';
const JOIN_TILE = 'JoinTiles';

const findLabel = (
  label: string,
  navigationCollection?: StaticNavigationNavigationItemDto[] | null,
) =>
  navigationCollection?.find((navigationElement) => navigationElement.type === label)?.title ?? '';

const findUrl = (
  label: string,
  navigationCollection?: StaticNavigationNavigationItemDto[] | null,
) => navigationCollection?.find((navigationElement) => navigationElement.type === label)?.url;

export const NavigationContext = createContext<NavigationContextValues | undefined>(undefined);

export const NavigationContextProvider: FC = ({ children }) => {
  const [activeLink, setActiveLink] = useState<Navigation | null>(null);
  const [pbsContentIsShown, setPbsContentIsShown] = useState(false);
  const [activeMenu, setActiveMenu] = useState<ToggleMenuFlyout.MenuTypes | ''>('');
  const [aboutLabel, setAboutLabel] = useState('');
  const [aboutTiles, setAboutTiles] = useState<StaticNavigationNavigationItemDto[]>([]);
  const [joinLabel, setJoinLabel] = useState('');
  const [joinUrl, setJoinUrl] = useState<string | undefined>('');
  const [isFetching, setIsFetching] = useState(false);
  const [burgerMenuTab, setBurgerMenuTab] = useState<BurgerMenuTabNames>(
    BurgerMenuTabNames.ProductsTab,
  );
  const [isSearchInputFocus, setIsSearchInputFocus] = useState(false);
  const [isSearchInputVisible, setIsSearchInputVisible] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<SuggestionsAndResults | null>(null);
  const [searchTimestamp, setSearchTimestamp] = useState(0);
  const [joinTiles, setJoinTiles] = useState<StaticNavigationNavigationItemDto[]>([]);
  const [iDontKnowYetLink, setIDontKnowYetLink] = useState<
    StaticNavigationNavigationItemDto | null | undefined
  >(null);
  const [inspirationTiles, setInspirationTiles] = useState<StaticNavigationNavigationItemDto[]>([]);
  const [shopSection, setShopSection] = useState<StaticNavigationNavigationItemDto[]>([]);
  const [cancelToken, setCancelToken] = useState<CancelTokenSource | undefined>();
  const [isNavigationsFetched, setNavigationsElementsFetcher] = useState(false);
  const [isMenuDownloaded, setIsMenuDownloaded] = useState(false);
  const isMobile = useMobileWidth();

  const { currentLanguage } = useAppConfigurationContext();

  const getNavigationData = useFetchNavigationItems();

  const updateActiveLinkAction = useCallback(
    (link: Navigation | null) => {
      // We make sure that on link change is pbs hidden
      setPbsContentIsShown(false);
      if (isMobile && activeLink === link) {
        setActiveLink(null);
      } else {
        setActiveLink(link);
      }
    },
    [activeLink, isMobile],
  );

  const getTopNavigationAttributes = useCallback(async () => {
    const response = await getNavigationData('TopNavigation');

    setAboutLabel(findLabel(ABOUT_TILE, response));
    setJoinLabel(findLabel(JOIN_TILE, response));
    setJoinUrl(findUrl(JOIN_TILE, response));
  }, [getNavigationData]);

  const getElements = useCallback(
    async (navigationType: NavigationType): Promise<void> => {
      const response = await getNavigationData(navigationType);

      switch (navigationType) {
        case NavigationTypes.AboutTiles: {
          setAboutTiles(response);
          break;
        }
        case NavigationTypes.ShopSection: {
          setIsMenuDownloaded(true);
          setShopSection(response);
          break;
        }
        case NavigationTypes.InspirationTiles: {
          setInspirationTiles(response);
          break;
        }
        case NavigationTypes.JoinTiles: {
          setJoinTiles(response.filter((tile) => tile.linkType === JoinLinks.JoinTile));
          setIDontKnowYetLink(
            response.find((link) => link.linkType === JoinLinks.IDontKnowYetLink),
          );
          break;
        }
        default: {
          throw new Error('Unknown collection');
        }
      }
    },
    [getNavigationData],
  );

  const areNavigationsElementsFetched = useCallback(
    () => isNavigationsFetched,
    [isNavigationsFetched],
  );

  const getNavigationElements = useCallback(() => {
    try {
      setNavigationsElementsFetcher(true);

      Object.values(NavigationTypes).forEach((element) => {
        if (element === NavigationTypes.ShopSection) {
          setIsMenuDownloaded(false);
        }
        getElements(element);
      });

      getTopNavigationAttributes();
    } catch (error: unknown) {
      console.warn(error);
    }
  }, [getElements, getTopNavigationAttributes]);

  const openBurgerMenuTab = (tab: BurgerMenuTabNames): void => {
    setBurgerMenuTab(tab);
  };

  const toggleIsSearchInputVisible = useCallback((): void => {
    setIsSearchInputVisible(!isSearchInputVisible);
  }, [isSearchInputVisible]);

  const getSearchResults = useCallback(
    async (query: string) => {
      if (!query) {
        return;
      }

      try {
        if (cancelToken) {
          cancelToken.cancel();
        }

        const newCancelToken = axios.CancelToken.source();
        setCancelToken(newCancelToken);

        const url = new URL(`${window.location.origin}/system/ajax/Search/SuggestionsAndResults`);
        url.searchParams.set('q', query);

        const { data } = await getCustomAxios(currentLanguage).get<SuggestionsAndResults>(
          url.toString(),
          {
            cancelToken: newCancelToken.token,
          },
        );

        if (searchInputValue.trim() === data.Query) {
          setSearchResults(data);
          setSearchTimestamp(performance.now());
        }

        setIsFetching(false);
      } catch (error: unknown) {
        if (axios.isCancel(error)) {
          console.log('Search request canceled', error);
        } else {
          console.log(error);
        }
      } finally {
        setCancelToken(undefined);
      }
    },
    [cancelToken, currentLanguage, searchInputValue],
  );

  const toggleMenu = useCallback(
    (menuType: ToggleMenuFlyout.MenuTypes | ''): void => {
      if (menuType === ToggleMenuFlyout.MenuTypes.SearchInput) {
        toggleIsSearchInputVisible();
      } else if (activeMenu === menuType) {
        setActiveMenu('');
      } else {
        setActiveMenu(menuType);
      }
    },
    [activeMenu, toggleIsSearchInputVisible],
  );

  const hideSearchResultsTab = useCallback((): void => {
    setSearchInputValue('');
    setIsSearchInputFocus(false);
    toggleMenu('');
    setSearchResults(null);
    setIsSearchInputVisible(false);
  }, [toggleMenu]);

  const clearSearchResults = (): void => {
    setSearchResults(null);
  };

  const startFetchingSearchData = (): void => {
    setIsFetching(true);
  };

  const value: NavigationContextValues = useMemo<NavigationContextValues>(
    () => ({
      aboutLabel,
      aboutTiles,
      activeLink,
      activeMenu,
      areNavigationsElementsFetched,
      burgerMenuTab,
      clearSearchResults,
      getNavigationElements,
      getSearchResults,
      hideSearchResultsTab,
      iDontKnowYetLink,
      inspirationTiles,
      isFetching,
      isMenuDownloaded,
      isSearchInputFocus,
      isSearchInputVisible,
      joinLabel,
      joinTiles,
      joinUrl,
      openBurgerMenuTab,
      searchInputValue,
      searchResults,
      searchTimestamp,
      shopSection,
      setIsMenuDownloaded,
      setIsSearchInputFocus,
      setSearchInputValue,
      startFetchingSearchData,
      toggleIsSearchInputVisible,
      toggleMenu,
      pbsContentIsShown,
      setPbsContentIsShown,
      updateActiveLinkAction,
    }),
    [
      aboutLabel,
      aboutTiles,
      activeLink,
      activeMenu,
      areNavigationsElementsFetched,
      burgerMenuTab,
      getNavigationElements,
      getSearchResults,
      hideSearchResultsTab,
      iDontKnowYetLink,
      inspirationTiles,
      isFetching,
      isMenuDownloaded,
      isSearchInputFocus,
      isSearchInputVisible,
      joinLabel,
      joinTiles,
      joinUrl,
      searchInputValue,
      searchResults,
      searchTimestamp,
      shopSection,
      toggleIsSearchInputVisible,
      toggleMenu,
      pbsContentIsShown,
      updateActiveLinkAction,
    ],
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  if (context) {
    return context;
  }
  throw new Error('NavigationContext value must be set.');
};
